import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Minus, Plus, X } from "lucide-react";
import { WorkspaceShell, usePreviewReadOnly } from "@/components/workspace/WorkspaceShell";
import { getRuntimeConfig, setRuntimeConfig } from "@/lib/api";
import { validateTrialData, type TrialData, type TrialIssue, type TrialStage } from "@/lib/trial-schema";
import { diffTrialData } from "@/lib/trial-diff";
import { useDirtyStore } from "@/lib/dirty-store";
import { issuesForStage } from "@/lib/trial-issues";
import { addFloor, removeLastFloor, updateStageAt } from "@/lib/trial-edit";
import { monolithName } from "@/lib/trial-defense";
import { cn } from "@/lib/utils";
import { FloorDetail } from "./FloorDetail";
import { TrialLeaderboardPanel } from "./LeaderboardPanel";
import "./trial.css";

const TRIAL_KEY = "EC2_TRIAL_STAGE_3.0";
const errorText = (error: unknown) => error instanceof Error ? error.message : String(error);
const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? "" : "s"}`;

function prettify(raw: string): string {
  if (!raw) return "";
  try { return JSON.stringify(JSON.parse(raw), null, 2); } catch { return raw; }
}

/** Floor editor state. Lives in the page so edits survive switching to the leaderboard tab. */
function useTrialFloors() {
  const qc = useQueryClient();
  const setDirty = useDirtyStore(s => s.setDirty);
  const query = useQuery({ queryKey: ["runtime-config", TRIAL_KEY], queryFn: () => getRuntimeConfig(TRIAL_KEY) });
  const serverValue = query.data ?? "";

  // Serialized form state, seeded from the server once; edits stay local until Save round-trips.
  const [text, setText] = useState("");
  const [seeded, setSeeded] = useState(false);
  useEffect(() => {
    if (!seeded && query.isSuccess) { setText(prettify(serverValue)); setSeeded(true); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.isSuccess, seeded]);

  const dirty = seeded && text !== prettify(serverValue);
  useEffect(() => { setDirty(dirty); return () => setDirty(false); }, [dirty, setDirty]);
  useEffect(() => {
    if (!dirty) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [dirty]);

  // `data` is the form's single source of truth; every edit serializes straight back into `text`.
  const { parseError, issues, data } = useMemo(() => {
    if (!text.trim()) return { parseError: "Empty document.", issues: [] as TrialIssue[], data: null as TrialData | null };
    try {
      const parsed = JSON.parse(text) as TrialData;
      return { parseError: null, issues: validateTrialData(parsed), data: parsed };
    } catch (e) {
      return { parseError: e instanceof Error ? e.message : "Invalid JSON", issues: [] as TrialIssue[], data: null as TrialData | null };
    }
  }, [text]);
  const errors = issues.filter(i => i.severity === "error");
  const canSave = dirty && !parseError && errors.length === 0;

  const [selected, setSelected] = useState(0);
  useEffect(() => {
    if (data && selected >= data.stages.length) setSelected(Math.max(0, data.stages.length - 1));
  }, [data, selected]);

  const commit = (next: TrialData) => setText(JSON.stringify(next, null, 2));
  function updateStage(idx: number, updater: (s: TrialStage) => TrialStage) { if (data) commit(updateStageAt(data, idx, updater)); }
  function handleAddFloor() {
    if (!data) return;
    const next = addFloor(data);
    commit(next);
    setSelected(next.stages.length - 1);
  }
  function handleRemoveLastFloor() {
    if (!data || data.stages.length === 0) return;
    const lastFloor = data.stages[data.stages.length - 1].floor;
    if (!window.confirm(`Remove floor ${lastFloor}? This can only be undone by reverting to live.`)) return;
    commit(removeLastFloor(data));
  }

  const [showConfirm, setShowConfirm] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const diff = useMemo(() => diffTrialData(serverValue, text), [serverValue, text]);
  const mutation = useMutation({
    mutationFn: () => setRuntimeConfig(TRIAL_KEY, JSON.stringify(JSON.parse(text))),
    onSuccess: async () => {
      setShowConfirm(false);
      setConfirmText("");
      await qc.invalidateQueries({ queryKey: ["runtime-config", TRIAL_KEY] });
      setSeeded(false); // re-seed from the saved value so `dirty` clears
    },
  });
  function openConfirm() { setConfirmText(""); mutation.reset(); setShowConfirm(true); }

  return { query, data, parseError, issues, dirty, errors, canSave, selected, setSelected, updateStage, handleAddFloor, handleRemoveLastFloor,
    showConfirm, setShowConfirm, confirmText, setConfirmText, diff, mutation, openConfirm };
}
type Floors = ReturnType<typeof useTrialFloors>;

export function TrialWorkspace() {
  const readOnly = usePreviewReadOnly();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get("tab") === "leaderboard" ? "leaderboard" : "floors";
  const floors = useTrialFloors();
  function selectTab(next: "floors" | "leaderboard") {
    const params = new URLSearchParams(searchParams);
    if (next === "leaderboard") params.set("tab", "leaderboard"); else params.delete("tab");
    setSearchParams(params);
  }

  return <WorkspaceShell crumbs={[{ label: "Game configuration" }, { label: "Chamber of Trial" }]} readOnly={readOnly}>
    <div className="ws-scroll"><div className="ws-page trial-page">
      <div className="ws-head tr-head">
        <h1>Chamber of Trial</h1>
        <div className="ws-segmented" role="tablist" aria-label="Chamber of Trial sections">
          {(["floors", "leaderboard"] as const).map(key => <button key={key} role="tab" aria-selected={tab === key} className={cn(tab === key && "is-active")} onClick={() => selectTab(key)}>
            {key === "floors" ? "Floors" : "Leaderboard"}</button>)}
        </div>
        {tab === "floors" && floors.dirty && <div className="ws-head-actions">
          <button className="pw-button pw-button-primary" onClick={floors.openConfirm} disabled={!floors.canSave || readOnly}
            title={floors.errors.length > 0 ? "Fix the errors first" : undefined}>Save</button>
        </div>}
      </div>
      <div role="tabpanel" aria-label="Floors" hidden={tab !== "floors"}><FloorsPanel floors={floors} /></div>
      {tab === "leaderboard" && <div role="tabpanel" aria-label="Leaderboard"><TrialLeaderboardPanel readOnly={readOnly} /></div>}
    </div></div>
    {floors.showConfirm && <ConfirmSaveDialog floors={floors} readOnly={readOnly} />}
  </WorkspaceShell>;
}

function FloorsPanel({ floors }: { floors: Floors }) {
  const { query, data, issues, selected } = floors;
  if (query.isPending) return <div className="pw-empty" role="status"><span className="pw-loading-dot" />Loading floors…</div>;
  if (query.isError) return <div className="ws-alert" role="alert">Failed to load {TRIAL_KEY}: {errorText(query.error)}. Import this key from PlayFab first.</div>;
  if (!data) return <div className="ws-alert" role="alert">Can't open the Chamber data: {floors.parseError ?? "Unknown error"}</div>;
  const stage = data.stages[selected];
  const globalErrors = issues.filter(i => i.severity === "error" && !i.path.startsWith("stages["));
  return <>{globalErrors.length > 0 && <div className="ws-alert" role="alert">{globalErrors.map(i => i.message).join(" ")}</div>}<div className="pw-heroes tr-layout">
    <div className="tr-side">
      <div className="pw-hero-picker tr-picker" role="group" aria-label="Select floor">{data.stages.map((s, i) => {
        const hasError = issuesForStage(issues, i).some(x => x.severity === "error");
        return <button key={i} onClick={() => floors.setSelected(i)} aria-pressed={i === selected} className={cn("pw-hero-option", i === selected && "is-active")}>
          <span className="pw-category-icon tr-floor-num">{s.floor}</span>
          <span className="tr-option-text"><strong>{monolithName(s.defenseSystem)}</strong></span>
          {hasError && <span className="tr-error-dot" title="Has errors" aria-label="Has errors" />}
        </button>;
      })}</div>
      <div className="tr-side-actions">
        <button className="pw-button" onClick={floors.handleAddFloor}><Plus size={13} />Add floor</button>
        <button className="pw-button pw-button-danger" onClick={floors.handleRemoveLastFloor} disabled={data.stages.length === 0} title="Remove last floor"><Minus size={13} />Last</button>
      </div>
    </div>
    <div className="pw-hero-content">
      {stage ? <FloorDetail stage={stage} issues={issuesForStage(issues, selected)} onUpdate={updater => floors.updateStage(selected, updater)} />
        : <div className="ws-empty">No floors yet</div>}
    </div>
  </div></>;
}

function ConfirmSaveDialog({ floors, readOnly }: { floors: Floors; readOnly: boolean }) {
  const { diff, mutation, confirmText, setConfirmText } = floors;
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => { const dialog = ref.current; if (dialog && !dialog.open) dialog.showModal(); return () => dialog?.close(); }, []);
  const saving = mutation.isPending;
  const cancel = () => { if (!saving) floors.setShowConfirm(false); };
  const confirmed = confirmText.trim() === TRIAL_KEY;
  const noChanges = !diff.unparseable && !diff.leaderboardVersionChanged && diff.floors.length === 0;

  return <dialog ref={ref} className="ws-dialog tr-confirm" aria-label={`Confirm write to ${TRIAL_KEY}`}
    onCancel={e => { e.preventDefault(); cancel(); }} onClick={e => { if (e.target === e.currentTarget) cancel(); }}>
    <div className="tr-confirm-inner">
      <div className="ws-dialog-head"><h2>Write to <span className="ws-mono">{TRIAL_KEY}</span></h2>
        <button className="pw-icon-button" aria-label="Close" onClick={cancel} disabled={saving}><X size={16} /></button></div>
      <div className="ws-dialog-body">
        {diff.leaderboardVersionChanged && <div className="ws-alert" role="alert">
          <strong>leaderboardVersion {diff.leaderboardVersionBefore ?? "?"} → {diff.leaderboardVersionAfter ?? "?"}</strong>: season reset. <code>ResetProgression()</code> wipes
          every player's cleared floors, stars and best times.</div>}
        {diff.unparseable ? <span className="ws-muted">Could not compute a floor-level diff.</span>
          : noChanges ? <span className="ws-muted">No floors changed. No-op write.</span>
          : <div className="tr-diff-list">
            <span className="ws-muted">{plural(diff.floors.length, "floor")} changed</span>
            {diff.floors.map(f => <div key={f.floor} className="tr-diff">
              <div className="tr-diff-head">Floor {f.floor}<span className={cn("pw-badge", f.kind === "removed" && "pw-badge-danger", f.kind === "added" && "tr-badge-ok")}>{f.kind}</span></div>
              <div className="tr-diff-body"><pre className="is-before" aria-label="Before">{f.before ?? "(none)"}</pre><pre className="is-after" aria-label="After">{f.after ?? "(none)"}</pre></div>
            </div>)}
          </div>}
        {mutation.error != null && <div className="ws-alert" role="alert">{errorText(mutation.error)}</div>}
        <label className="ws-field ws-form">Type {TRIAL_KEY} to confirm
          <input className="ws-mono tr-confirm-input" value={confirmText} onChange={e => setConfirmText(e.target.value)} placeholder={TRIAL_KEY} autoFocus spellCheck={false} autoComplete="off" />
        </label>
      </div>
      <div className="ws-dialog-foot">
        <button className="pw-button" onClick={cancel} disabled={saving}>Cancel</button>
        <button className="pw-button pw-button-danger tr-solid-danger" onClick={() => mutation.mutate()} disabled={!confirmed || saving || readOnly}>{saving ? "Writing…" : "Write to PlayFab"}</button>
      </div>
    </div>
  </dialog>;
}
